import { apiConstants } from "@data/constants/apiConstants"
import { authConstants } from "@data/constants/authConstants"
import { authService } from "@data/services/authService"
import { fastTrackService } from "@data/services/fastTrackService"

const initialState = {
    storedTokens: [],
}

export function authTokens(state = initialState, action) {
    switch (action.type) {
        case authConstants.SAVE_AUTH_TOKEN: {
            // La coquille enregistre le jeton de la session sous le compte affiché, deux valeurs lues
            // séparément. Elle ne donne donc jamais un jeton déjà gardé sous un autre compte, ni ne
            // remplace celui qu'un compte garde (les réponses du serveur les donnent avec leur compte) —
            // sauf une session impersonnée, dont l'amorce a posé le jeton et le compte ensemble.
            const heldByAnotherAccount = state.storedTokens.some(
                (tokenData) =>
                    tokenData?.token === action.tokenData?.token &&
                    tokenData?.userId !== action.tokenData?.userId
            )
            if (heldByAnotherAccount) return state
            const existingIndex = state.storedTokens.findIndex(
                (tokenData) => tokenData?.userId === action.tokenData?.userId
            )

            if (existingIndex !== -1) {
                const existing = state.storedTokens[existingIndex]
                const updatedTokens = [...state.storedTokens]
                updatedTokens[existingIndex] = {
                    ...existing,
                    ...action.tokenData,
                    token:
                        action.tokenData?.is_impersonation || !existing.token
                            ? action.tokenData?.token
                            : existing.token,
                    lastConnected: new Date().toISOString(),
                }
                return {
                    ...state,
                    storedTokens: updatedTokens,
                }
            } else {
                return {
                    ...state,
                    storedTokens: [
                        ...state.storedTokens,
                        {
                            ...action.tokenData,
                            lastConnected: new Date().toISOString(),
                        },
                    ],
                }
            }
        }

        case authConstants.SWITCH_ACCOUNT: {
            // Update lastConnected when switching accounts
            const existingIndex = state.storedTokens.findIndex(
                (tokenData) => tokenData?.userId === action.tokenData?.userId
            )

            if (existingIndex !== -1) {
                const updatedTokens = [...state.storedTokens]
                const date = new Date()
                updatedTokens[existingIndex] = {
                    ...updatedTokens[existingIndex],
                    lastConnected: date.toISOString(),
                }

                return {
                    ...state,
                    storedTokens: updatedTokens,
                }
            }
            return state
        }

        case authConstants.REMOVE_AUTH_TOKEN:
            return {
                ...state,
                storedTokens: state.storedTokens.filter(
                    (tokenData) => tokenData?.userId !== action.userId
                ),
            }

        case authConstants.UPDATE_LAST_CONNECTED: {
            const existingIndex = state.storedTokens.findIndex(
                (tokenData) => tokenData?.userId === action.user?.id
            )

            if (existingIndex !== -1) {
                const updatedTokens = [...state.storedTokens]
                updatedTokens[existingIndex] = {
                    ...updatedTokens[existingIndex],
                    lastConnected: new Date().toISOString(),
                }
                return {
                    ...state,
                    storedTokens: updatedTokens,
                }
            }

            return state
        }

        case authService.bootstrapImpersonation.name + apiConstants._SUCCESS:
        case authService.exitImpersonation.name + apiConstants._SUCCESS:
            // Drop only the impersonation-typed entries — both at the start
            // of a new impersonated session (bootstrap) and on exit. This
            // clears stale impersonation tokens left over when a previous
            // tab was closed without clicking Quitter, while preserving
            // admin's own pre-existing non-impersonation accounts.
            return {
                ...state,
                storedTokens: state.storedTokens.filter(
                    (t) => !t?.is_impersonation
                ),
            }

        // A 401 on exit means the impersonation token already died (2 h TTL): the session
        // is gone all the same, so its entries leave like on a successful exit.
        case authService.exitImpersonation.name + apiConstants._FAILURE:
            if (action.error?.status !== 401) return state
            return {
                ...state,
                storedTokens: state.storedTokens.filter(
                    (t) => !t?.is_impersonation
                ),
            }

        case authService.getFamilyTokens.name + apiConstants._SUCCESS:
        case authService.validateToken.name + apiConstants._SUCCESS:
        case authService.getSanctumToken.name + apiConstants._SUCCESS:
        case authService.verifyLoginCode.name + apiConstants._SUCCESS:
        case authConstants.ACCEPT_LOGIN_LINK:
        case fastTrackService.verifyRecognition.name + apiConstants._SUCCESS: {
            // The last one is « On vous connaît déjà » on the fast-track: the
            // family proved who it is, the device keeps its accounts, but
            // nobody is switched in.
            //
            // The `impersonation` block is only emitted on the requesting
            // user's UserResource (sibling blocks come back null by design),
            // so its presence on `data.user` is a reliable per-response
            // signal that the *whole* response — including family_tokens —
            // was minted under impersonation.
            const isImpersonation = !!action.data?.user?.impersonation

            const mainUserTokenData = {
                email: action.data.user.email,
                userId: action.data.user.id,
                displayName: action.data.user.display_name,
                firstname: action.data.user.firstname,
                lastname: action.data.user.lastname,
                roles: action.data.user.roles,
                avatar: action.data.user.avatar,
                lastConnected: new Date().toISOString(),
                hubspot_id: action.data.user.hubspot_id,
                is_impersonation: isImpersonation,
            }

            // Only add token if it exists in the response
            if (action.data.token) {
                mainUserTokenData.token = action.data.token
            }

            // Only add title_skill_set_id if it exists in the response
            if (action.data.user.title_skill_set_id !== undefined) {
                mainUserTokenData.title_skill_set_id =
                    action.data.user.title_skill_set_id
            }

            // Blocs éteints dans l'espace de l'élève : la sélection de compte
            // les lit avant que l'utilisateur complet soit chargé.
            if (action.data.user.disabled_blocks !== undefined) {
                mainUserTokenData.disabled_blocks =
                    action.data.user.disabled_blocks
            }

            let newStoredTokens = [...state.storedTokens]

            // Update or add main user token
            const mainUserIndex = newStoredTokens.findIndex(
                (tokenData) => tokenData?.userId === mainUserTokenData.userId
            )

            if (mainUserIndex !== -1) {
                // Merge with existing token to preserve fields not in API response
                newStoredTokens[mainUserIndex] = {
                    ...newStoredTokens[mainUserIndex], // Keep existing data
                    ...mainUserTokenData, // Override with new data
                }
            } else {
                newStoredTokens.push(mainUserTokenData)
            }

            // Save family tokens if they exist
            if (
                action.data.family_tokens &&
                action.data.family_tokens.length > 0
            ) {
                action.data.family_tokens.forEach((familyToken) => {
                    // Find existing token to preserve lastConnected
                    const existingToken = newStoredTokens.find(
                        (tokenData) => tokenData?.userId === familyToken.user.id
                    )

                    const familyTokenData = {
                        email: familyToken.user.email,
                        userId: familyToken.user.id,
                        displayName:
                            familyToken.user.firstname +
                            " " +
                            familyToken.user.lastname,
                        firstname: familyToken.user.firstname,
                        lastname: familyToken.user.lastname,
                        roles: familyToken.user.roles,
                        avatar: familyToken.user.avatar,
                        avatar_selected: familyToken.user.avatar_selected,
                        hubspot_id: familyToken.user.hubspot_id,
                        lastConnected:
                            existingToken?.lastConnected ||
                            new Date().toISOString(),
                        is_impersonation: isImpersonation,
                    }

                    // Only add token if it exists in the response
                    if (familyToken.token) {
                        familyTokenData.token = familyToken.token
                    }

                    // Only add title_skill_set_id if it exists in the response
                    if (familyToken.user.title_skill_set_id !== undefined) {
                        familyTokenData.title_skill_set_id =
                            familyToken.user.title_skill_set_id
                    }

                    if (familyToken.user.disabled_blocks !== undefined) {
                        familyTokenData.disabled_blocks =
                            familyToken.user.disabled_blocks
                    }

                    // Update or add family token
                    const familyIndex = newStoredTokens.findIndex(
                        (tokenData) =>
                            tokenData?.userId === familyTokenData.userId
                    )

                    if (familyIndex !== -1) {
                        // Merge with existing token to preserve fields not in API response
                        newStoredTokens[familyIndex] = {
                            ...newStoredTokens[familyIndex], // Keep existing data
                            ...familyTokenData, // Override with new data
                        }
                    } else {
                        newStoredTokens.push(familyTokenData)
                    }
                })
            }

            return {
                ...state,
                storedTokens: newStoredTokens,
            }
        }

        case authConstants.LOGOUT_401:
        case authService.logout.name + apiConstants._SUCCESS:
        case authService.revokeSanctumToken.name + apiConstants._SUCCESS:
        case authService.deleteAccount.name + apiConstants._SUCCESS:
            return state

        default:
            return state
    }
}
